import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { DATE_FORMAT } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { CircleOutlinedIcon } from '@root/shared/ui-kit'

import {
  LIMIT_ORDER_STATUS_COLOR_MAP,
  LIMIT_ORDER_STATUS_LABEL_MAP,
} from '../../constants'
import { useLimitOrderEndDateFormatted } from '../../hooks'

import type { LimitOrderModel } from '../../../atoms'
import type { LimitOrder } from '../../../model'

type Props = {
  order: LimitOrderModel
}

export const LimitOrderItemFooter: React.FC<Props> = ({ order }) => {
  const { t } = useTranslation()
  const { formatEndDate } = useLimitOrderEndDateFormatted()
  const [status] = useAtom(order.status)
  const [closedAt] = useAtom(order.closedAt)

  const dateFormatted = useMemo(
    () =>
      formatEndDate({
        closedAt,
        deadlineAt: order.deadlineAt,
        status,
      }),
    [closedAt, formatEndDate, order.deadlineAt, status],
  )

  return (
    <Root>
      <Date>
        {t('order.creation-date')}:{' '}
        {DateF.format(order.createdAt, DATE_FORMAT.dd_MM_yy)}
      </Date>
      <Date>{dateFormatted}</Date>
      <Status $status={status}>
        <CircleOutlinedIcon size={8} />
        {t(LIMIT_ORDER_STATUS_LABEL_MAP[status])}
      </Status>
    </Root>
  )
}

const Root = styled.footer`
  display: grid;

  grid-template-columns: minmax(110px, auto) 1fr auto;
  padding-inline: 10px;
  align-items: center;
  justify-content: space-between;

  height: 26px;
  gap: 10px;
`

const Date = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'span',
})`
  color: var(--text-9);
  text-align: left;
`

const Status = styled(Typography).attrs({
  variant: 'description-semi-bold',
  component: 'span',
})<{ $status: LimitOrder.Status }>`
  color: ${({ $status }) => LIMIT_ORDER_STATUS_COLOR_MAP[$status]};
  text-align: right;

  text-transform: uppercase;
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: end;
  gap: 4px;
`
